import { useState } from "react";
import { X, Save, Loader2, RefreshCw, Plus, Code2, Users, CalendarRange } from "lucide-react";

export default function ProjectEditModal({ project, onClose, onSave, onReanalyze }) {
  const initialTech = Array.isArray(project.techStackOverride) && project.techStackOverride.length > 0
    ? project.techStackOverride
    : (project.analysis?.techStack_reco || []);
  const initialDuration = project.maxDurationWeeks
    || (project.analysis?.milestones || []).reduce((s, m) => s + (m.weeks || 0), 0)
    || "";
  const [form, setForm] = useState({
    projectTitle: project.projectTitle || "",
    projectCategory: project.projectCategory || "Web Development",
    projectDescription: project.projectDescription || "",
    techStackOverride: initialTech,
    teamSize: project.teamSize || 1,
    maxDurationWeeks: initialDuration,
  });
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [techDraft, setTechDraft] = useState("");

  const CATEGORIES = ["Web Development", "Mobile App", "3D/CAD Design"];

  const buildPayload = () => ({
    projectTitle: form.projectTitle,
    projectCategory: form.projectCategory,
    projectDescription: form.projectDescription,
    techStackOverride: form.techStackOverride.length > 0 ? form.techStackOverride : null,
    teamSize: form.teamSize ? parseInt(form.teamSize) : null,
    maxDurationWeeks: form.maxDurationWeeks ? parseInt(form.maxDurationWeeks) : null,
  });

  const handleSave = async () => {
    setSaving(true);
    await onSave(buildPayload());
    setSaving(false);
  };

  const handleSaveAndReanalyze = async () => {
    setReanalyzing(true);
    await onSave(buildPayload());
    await onReanalyze();
    setReanalyzing(false);
  };

  const addTech = () => {
    const t = techDraft.trim();
    if (!t || form.techStackOverride.includes(t)) return;
    setForm(p => ({ ...p, techStackOverride: [...p.techStackOverride, t] }));
    setTechDraft("");
  };
  const removeTech = (t) =>
    setForm(p => ({ ...p, techStackOverride: p.techStackOverride.filter(x => x !== t) }));

  const aiMilestoneTotal = (project.analysis?.milestones || []).reduce((s, m) => s + (m.weeks || 0), 0);

  const hasChanges =
    form.projectTitle !== project.projectTitle ||
    form.projectCategory !== project.projectCategory ||
    form.projectDescription !== project.projectDescription ||
    JSON.stringify(form.techStackOverride) !== JSON.stringify(project.techStackOverride || project.analysis?.techStack_reco || []) ||
    parseInt(form.teamSize) !== (project.teamSize || 1) ||
    parseInt(form.maxDurationWeeks) !== (project.maxDurationWeeks || aiMilestoneTotal);

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 99999 }} onClick={onClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative glass-surface neon-border rounded-lg p-6 max-w-2xl w-full animate-fade-in-up max-h-[92vh] overflow-y-auto"
        data-testid="project-edit-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6 sticky top-0 -mt-2 pt-2 bg-[#0F0F16]/95 backdrop-blur z-10 pb-3 -mx-6 px-6 border-b border-white/5">
          <h2 className="font-['Outfit'] text-xl font-medium text-white">Projekt bearbeiten</h2>
          <button data-testid="edit-modal-close" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors">
            <X className="w-4 h-4 text-[#A0A0B0]" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-5">
          <div>
            <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Projekttitel</label>
            <input
              data-testid="edit-title-input"
              type="text"
              value={form.projectTitle}
              onChange={e => setForm(p => ({ ...p, projectTitle: e.target.value }))}
              className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 font-['JetBrains_Mono'] text-sm transition-colors outline-none"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Projektkategorie</label>
            <select
              data-testid="edit-category-select"
              value={form.projectCategory}
              onChange={e => setForm(p => ({ ...p, projectCategory: e.target.value }))}
              className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 font-['JetBrains_Mono'] text-sm transition-colors outline-none appearance-none cursor-pointer"
            >
              {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#0F0F16]">{c}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-[#A0A0B0] mb-1.5 font-['Outfit']">Projektbeschreibung</label>
            <textarea
              data-testid="edit-description-textarea"
              value={form.projectDescription}
              onChange={e => setForm(p => ({ ...p, projectDescription: e.target.value }))}
              rows={5}
              className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-4 py-3 placeholder:text-white/30 font-['JetBrains_Mono'] text-sm transition-colors outline-none resize-none"
            />
          </div>

          {/* Constraints — User-Wünsche */}
          <div className="rounded-lg border border-[#00F0FF]/15 bg-[#00F0FF]/[0.03] p-4 space-y-4">
            <div className="flex items-center gap-2 text-[10px] font-['JetBrains_Mono'] text-[#00F0FF] uppercase tracking-widest">
              <Code2 className="w-3.5 h-3.5" /> Eigene Vorgaben (überschreiben KI-Empfehlung)
            </div>

            {/* Tech Stack Override */}
            <div>
              <label className="block text-xs text-[#A0A0B0] mb-1.5 font-['Outfit']">
                Empfohlene Technologien
                <span className="ml-2 text-[10px] text-[#606070]">(Tags hinzufügen, Enter)</span>
              </label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {form.techStackOverride.map((t) => (
                  <span
                    key={t}
                    data-testid={`edit-tech-tag-${t}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-['JetBrains_Mono'] bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/30"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeTech(t)}
                      className="ml-1 hover:text-white"
                      aria-label={`Remove ${t}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {form.techStackOverride.length === 0 && (
                  <span className="text-[11px] text-[#606070] italic">Keine Vorgabe – KI wählt automatisch.</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  data-testid="edit-tech-input"
                  type="text"
                  value={techDraft}
                  onChange={e => setTechDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); addTech(); }
                  }}
                  placeholder="z.B. Next.js, Python/FastAPI, PostgreSQL"
                  className="flex-1 bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] px-3 py-2 font-['JetBrains_Mono'] text-xs transition-colors outline-none rounded"
                />
                <button
                  type="button"
                  data-testid="edit-tech-add-btn"
                  onClick={addTech}
                  className="px-3 py-2 rounded bg-[#00F0FF]/15 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/25 transition-colors"
                  aria-label="Technologie hinzufügen"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Team Size */}
              <div>
                <label className="block text-xs text-[#A0A0B0] mb-1.5 font-['Outfit'] flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Teamgröße
                </label>
                <input
                  data-testid="edit-team-size"
                  type="number"
                  min="1"
                  max="20"
                  value={form.teamSize}
                  onChange={e => setForm(p => ({ ...p, teamSize: e.target.value }))}
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] px-3 py-2 font-['JetBrains_Mono'] text-sm transition-colors outline-none rounded"
                />
                <p className="text-[10px] text-[#606070] mt-1">{form.teamSize} Person{parseInt(form.teamSize) === 1 ? "" : "en"} parallel</p>
              </div>

              {/* Max Duration Weeks */}
              <div>
                <label className="block text-xs text-[#A0A0B0] mb-1.5 font-['Outfit'] flex items-center gap-1.5">
                  <CalendarRange className="w-3.5 h-3.5" /> Max. Projektdauer (Wochen)
                </label>
                <input
                  data-testid="edit-max-duration"
                  type="number"
                  min="1"
                  max="260"
                  value={form.maxDurationWeeks}
                  onChange={e => setForm(p => ({ ...p, maxDurationWeeks: e.target.value }))}
                  placeholder={aiMilestoneTotal ? `KI: ${aiMilestoneTotal} Wo.` : "z.B. 12"}
                  className="w-full bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] px-3 py-2 font-['JetBrains_Mono'] text-sm transition-colors outline-none rounded"
                />
                <p className="text-[10px] text-[#606070] mt-1">
                  KI-Schätzung: {aiMilestoneTotal || "?"} Wo.
                  {form.maxDurationWeeks && parseInt(form.maxDurationWeeks) < aiMilestoneTotal &&
                    ` · Cap unter Schätzung – Phasen werden gekürzt.`}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-white/5 flex-wrap">
          <button
            data-testid="edit-save-btn"
            onClick={handleSave}
            disabled={saving || reanalyzing || !form.projectTitle.trim()}
            className="btn-secondary flex items-center gap-2 text-sm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Speichern
          </button>

          <button
            data-testid="edit-reanalyze-btn"
            onClick={handleSaveAndReanalyze}
            disabled={saving || reanalyzing || !form.projectTitle.trim()}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            {reanalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {reanalyzing ? "KI analysiert neu..." : "Speichern & Neu analysieren"}
          </button>

          <button onClick={onClose} className="ml-auto text-sm text-[#606070] hover:text-white transition-colors font-['Outfit']">
            Abbrechen
          </button>
        </div>

        {hasChanges && (
          <p className="text-[10px] text-[#FFB800] mt-3 font-['JetBrains_Mono']">
            Ungespeicherte Änderungen vorhanden
          </p>
        )}
      </div>
    </div>
  );
}
